import { Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { selectPowerBIIsLoading, selectReports } from '@store/selectors/power-bi/power-bi.selectors';
import { Report } from '@models/powerBIEmbed.model';
import { CPG_REPORT_CATEGORY } from '@constants/power-bi-report-categories';

export type HubTile =
  | { kind: 'report'; report: Report }
  | { kind: 'category'; label: string; icon: string; route: string[] };

@Component({
  selector: 'app-power-bi-reports-hub',
  imports: [AsyncPipe, MatButtonModule, MatIconModule, MatProgressSpinnerModule],
  templateUrl: './power-bi-reports-hub.component.html',
  styleUrl: './power-bi-reports-hub.component.css',
})
export class PowerBiReportsHubComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  readonly isLoading$ = this.store.select(selectPowerBIIsLoading);

  readonly hubTiles$ = this.store.select(selectReports).pipe(
    map((reports): HubTile[] => {
      const isCpgReport = (report: Report) => report.category === CPG_REPORT_CATEGORY;

      const reportTiles: HubTile[] = reports
        .filter(report => !isCpgReport(report))
        .map(report => ({ kind: 'report', report }));

      const hasCpgReports = reports.some(isCpgReport);
      if (!hasCpgReports) {
        return reportTiles;
      }

      const cpgCategoryTile: HubTile = {
        kind: 'category',
        label: 'Clinical Practice Guidelines (CPG) Reports',
        icon: 'grid_view',
        route: ['/bi', 'cpg'],
      };

      return [cpgCategoryTile, ...reportTiles];
    })
  );

  openTile(tile: HubTile): void {
    if (tile.kind === 'category') {
      this.router.navigate(tile.route);
    } else {
      this.router.navigate(['/bi', tile.report.reportId]);
    }
  }
}
